<template>
  <button
    type="button"
    align-left
    class="screen-hotkey-btn o-btn o-btn-align-left o-btn-block o-btn-outlined"
    :class="{ 'o-btn-active screen-hotkey-btn-active': active }"
    :active="active"
    @click="$emit('select', { value, code })"
    :data-hotkey="hotkey"
    :data-text="text"
    :value="value"
    role="radio"
    :aria-keyshortcuts="hotkey"
  >
    <span class="screen-hotkey-btn-inner">
      <ScreenHotkey :active="active" :hotkey="hotkey" />
      <span class="screen-hotkey-btn-inner-text">
        <span v-html="text" />
        <slot />
      </span>
      <span v-if="payment && id" class="screen-hotkey-btn-inner-logo">
        <PaymentLogo :logo="id" />
      </span>
    </span>
  </button>
</template>
<script setup lang="ts">
import ScreenHotkey from './ScreenHotkey.vue'
import PaymentLogo from './PaymentLogo.vue'

defineProps({
  hotkey: {
    type: String,
    required: true,
    default: null
  },
  value: {
    type: String,
    required: true,
    default: null
  },
  text: {
    type: String,
    required: true,
    default: null
  },
  id: {
    type: String,
    default: null
  },
  code: {
    type: String,
    default: null
  },
  active: {
    type: Boolean,
    default: false
  },
  payment: {
    type: Boolean,
    default: false
  }
})
</script>
<style lang="scss" scoped>
.o-btn {
  align-items: center;
  background-color: var(--color-bg-secondary);
  border: solid 2px $gray-500;
  border-radius: $border-radius-sm;
  box-sizing: border-box;
  color: var(--color-text-secondary);
  cursor: pointer;
  display: flex;
  font-family: $font-family-pilat-bold;
  font-size: $fs0;
  font-weight: 700;
  justify-content: center;
  min-height: 48px;
  opacity: 1;
  padding: 0 $spacer-3;
  position: relative;

  &:hover {
    background-color: var(--color-bg-secondary);
  }

  &:focus {
    &:before {
      border: dotted 1px $gray-100;
      border-radius: $border-radius;
      bottom: -5px;
      content: '';
      left: -5px;
      position: absolute;
      right: -5px;
      top: -5px;
    }
  }

  &.o-btn-align-left {
    justify-content: flex-start;
    text-align: left;
  }

  &.o-btn-block {
    display: block;
    width: 100%;
  }
}

/*
 * New Styles For Theme Support
*/

.o-btn {
  &.o-btn-active,
  &:active,
  &:hover,
  &:focus,
  &:focus-visible {
    border-color: var(--color-border-active);
  }

  &.o-btn-outlined {
    background-color: transparent;
    color: var(--color-text-primary);

    &:not(.o-btn-active) {
      border-color: var(--color-border-primary);
    }

    &:hover:not(.o-btn-active) {
      border-color: var(--color-border-secondary);
    }

    &.o-btn-active {
      border-color: var(--color-border-active);
    }

    &:active,
    &:focus,
    &:focus-visible,
    &.o-btn-pressed {
      border-color: var(--color-border-active);
    }
  }
}

.screen-hotkey-btn {
  margin-bottom: $spacer-2;
  height: fit-content;
  font-weight: 500; // Required for safari to match chrome

  &-active {
    animation-duration: 350ms;
    animation-name: activateButton;
  }

  &-inner-text {
    color: var(--color-text-unselected);
    letter-spacing: 0.32px;

    &-plan-details {
      color: var(--color-text-unselected);
    }
  }

  &-active &-inner-text {
    color: var(--color-text-primary);
  }

  &-inner {
    display: grid;
    grid-template-columns: 24fr 1fr;
    align-items: center;
    text-wrap: wrap;
    padding: $spacer-2 0;

    @media screen and (min-width: $grid-breakpoint-md) {
      display: grid;
      grid-template-columns: 24px 16fr 1fr;
      grid-column-gap: $spacer-3;
    }
  }
}

@keyframes activateButton {
  from {
    border-color: var(--color-border-active);
  }

  50% {
    border-color: transparent;
  }

  to {
    border-color: var(--color-border-active);
  }
}
</style>
